import React, { useEffect, useReducer, useRef, useState } from "react";
import { Button, DatePicker, Form, Input, InputNumber, Row, Select, Spin, Switch, Tag, Tooltip, Upload } from "antd";
import "./customform.less";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import _ from 'lodash';
import { initialState, reducer } from "./reducer";
import * as actions from './reducer/action';
import { validateImage } from "./helper";
import message from '../message';
import moment from "moment";

const UPLOAD_INSTANCE_KEYS = ['image', 'file'];
const DATE_INSTANCE_KEYS = ['date', 'datetime'];

const CustomForm = ({
	fields = [],
	onCancel = () => undefined,
	onSubmit = () => undefined,
	fileUploader = () => {
		return new Promise(() => {
			console.error('Assign File Upload handler');
		});
	},
	// Талбаруудыг татаж байх үед формыг Spin-ээр бүрхэнэ
	loading = false,
}) => {
	const [form] = Form.useForm();
	// onSubmit нь Promise буцаавал дуусах хүртэл "Хадгалах" товч loading байна
	const [submitting, setSubmitting] = useState(false);
	const mountedRef = useRef(true);

	useEffect(() => () => {
		mountedRef.current = false;
	}, []);

	const [ state, dispatch ] = useReducer(reducer, initialState, undefined);

	useEffect(() => {

		const values = {};
		const tagsClone = _.cloneDeep(state.tags);
		for (const field of fields) {
			if (field.hasOwnProperty('value')) {
				let value = field.value;
				if (DATE_INSTANCE_KEYS.includes(field.instance)) {
					value = moment(field.value);
				}
				if (field.instance === 'array' && !field.enum) {
					if (!tagsClone[field.key]) {
						tagsClone[field.key] = {
							inputValue: '',
							value: field.value ?? [],
						}
					}
				} else if (UPLOAD_INSTANCE_KEYS.includes(field.instance)) {
					value = [
						{
							url: field?.value || null,
						},
					];
				}
				values[field.key] = value;
			}
		}

		form.setFieldsValue(values);
		// Зөвхөн fields солигдоход утгыг дахин суулгана. state.tags-ыг deps-д оруулбал
		// хэрэглэгчийн оруулж буй tag бүрд формын утга дарагдана.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fields]);

	const getFormItem = (field, index) => {
		const customProps = field?.customProps ?? {};
		const formProps = {
			key: index,
			name: field.key,
			label: field.name,
			rules: [
				{
					required: field.instance !== "boolean" && !!field.required,
					message: `${field.name} дутуу байна!`,
				},
			],
			...customProps
		};
		const disabled = field.editLock || field.editable === false;

		if (field.hasOwnProperty("enum")) {
			// This means field is Dropdown/Select
			const options = [...field.enum];
			let filterOption;
			if (field.filterOption) {
				filterOption = (inputValue, option) => {
					const displayValue = field.enumDictionary?.[option.value] ?? option.value;
					try {
						return displayValue.toLowerCase().includes(inputValue.toLowerCase());
					} catch (e) {
						return true;
					}
				};
			} else {
				filterOption = field.filterOption;
			}

			return (
				<Form.Item {...formProps}>
					<Select
						showSearch
						disabled={disabled}
						mode={field.instance === "array" ? "multiple" : undefined}
						filterOption={filterOption}
					>
						{options.map((option) => {
							return (
								<Select.Option key={option} value={option}>
									{field.enumDictionary?.[option] ?? option}
								</Select.Option>
							);
						})}
					</Select>
				</Form.Item>
			);
		}
		switch (field.instance) {
			case "string": {
				return (
					<Form.Item {...formProps}>
						<Input disabled={disabled} />
					</Form.Item>
				);
			}
			case "boolean": {
				return (
					<Form.Item {...formProps} valuePropName="checked" initialValue={false}>
						<Switch />
					</Form.Item>
				);
			}
			case "number": {
				return (
					<Form.Item {...formProps}>
						<InputNumber disabled={disabled} />
					</Form.Item>
				);
			}
			case "date": {
				return (
					<Form.Item {...formProps}>
						<DatePicker
							disabled={disabled}
							disabledDate={field.disabledDate ?? undefined}
						/>
					</Form.Item>
				);
			}
			case 'datetime': {
				return (
					<Form.Item
						{...formProps}
					>
						<DatePicker
							disabled={disabled}
							disabledDate={field.disabledDate ?? undefined}
							showTime={{ format: 'HH:mm' }}
						/>
					</Form.Item>
				)
			}
			case "image": {
				return (
					<Form.Item {...formProps} valuePropName="fileList" getValueFromEvent={normFile}>
						<Upload
							onChange={e => handleImageChange(e, field)}
							listType="picture"
							beforeUpload={() => false}
							multiple={!!field.multiple}
							maxCount={field.multiple ? 10 : 1}
						>
							<Button icon={<UploadOutlined />}>Зураг хуулах</Button>
						</Upload>
					</Form.Item>
				);
			}
			case 'array': {
				const parent = {
					...formProps,
					name: undefined
				};
				const nested = {
					...formProps,
					label: undefined,
				};
				return (
					<Form.Item {...parent} key={`${field.key}_P`}>
						<Form.Item {...nested}>
							<Input.Group compact>
								<Input.TextArea
									allowClear
									autoSize
									style={{ width: 'calc(100% - 32px)', marginBottom: 10 }} // Note: 32px is icon width!
									onChange={e => handleTagInputChange(e.target.value, field?.key)}
									onPressEnter={e => {
										e.preventDefault();
										handleTagAdd(field.key)
									}}
									value={state.tags?.[field?.key]?.inputValue ?? ''}
								/>
								<Tooltip title="Нэмэх">
									<Button icon={<PlusOutlined onClick={() => handleTagAdd(field.key)} />} />
								</Tooltip>
							</Input.Group>
						</Form.Item>
						<div key={field.name + '1'}>
							{
								state.tags?.[field.key]?.value?.map?.((tag, index) => {
									return (
										<Tag
											key={`${field.key}_tag_${index}`}
											className='custom-form-tag'
											closable={true}
											onClose={e => handleTagClose(field.key, index, e)}
										>
											{tag}
										</Tag>
									)
								})
							}
						</div>
					</Form.Item>
				)
			}
			case 'textArea': {
				return (
					<Form.Item {...formProps}>
						<Input.TextArea disabled={disabled} autoSize/>
					</Form.Item>
				)
			}
			case 'file': {
				return (
					<Form.Item {...formProps} valuePropName="fileList" getValueFromEvent={normFile}>
						<Upload
							beforeUpload={() => false}
							multiple={!!field.multiple}
							maxCount={field.multiple ? 10 : 1}
						>
							<Button icon={<UploadOutlined />}>Хуулах</Button>
						</Upload>
					</Form.Item>
				);
			}
			case "custom": {
				return <Form.Item {...formProps}>{field.component}</Form.Item>;
			}
			default:
				return null;
		}
	};

	const getFields = () => {
		const fieldItems = [];
		for (const [index, field] of fields.entries()) {
			if (field.show !== false) {
				fieldItems.push(getFormItem(field, index));
			}
		}
		return fieldItems;
	};

	const handleSubmit = async fieldValues => {
		setSubmitting(true);
		try {
			const values = await finalizeValues(fieldValues);
			await onSubmit(values);
		} finally {
			// Амжилттай хадгалаад өөр хуудас руу шилжсэн бол state шинэчлэхгүй
			if (mountedRef.current) setSubmitting(false);
		}
	};

	const normFile = (e) => {
		if (Array.isArray(e)) {
			return e;
		}
		return e && e.fileList;
	};

	const handleTagInputChange = (value, key) => {
		if (key) {
			const tagsClone = _.cloneDeep(state.tags);
			tagsClone[key] = {
				...tagsClone[key],
				inputValue: value,
			};
			dispatch({
				type: actions.tagChange,
				payload: tagsClone
			});
		}
	};

	const handleTagAdd = key => {
		const tagsClone = _.cloneDeep(state.tags);
		const inputValue = tagsClone?.[key]?.inputValue ?? '';
		if (inputValue) {
			const value = tagsClone?.[key]?.value ?? [];
			value.push(inputValue);
			tagsClone[key] = {
				inputValue: '',
				value
			}
			dispatch({
				type: actions.tagChange,
				payload: tagsClone
			});
			form.setFieldsValue({
				[key]: value,
			});
		}
	};

	const handleTagClose = (key, index, event) => {
		event.preventDefault();
		const tagsClone = _.cloneDeep(state.tags);
		const value = tagsClone?.[key].value?.constructor === Array ? tagsClone?.[key].value : [];
		value.splice(index, 1);
		tagsClone[key].value = value;
		dispatch({
			type: actions.tagChange,
			payload: tagsClone
		});
		form.setFieldsValue({
			[key]: value,
		})
	};

	const handleFormValueChange = (changedValues) => {

	};

	const handleImageChange = (event, field) => {
		const { key } = field;
		const value = [];
		for (const fileObject of event.fileList) {
			const valid = validateImage(fileObject);
			if (valid) {
				value.push(fileObject);
			} else {
				message(`${fileObject.name} зургийн багтаамж зөвшөөрөгдсөн хэмжээнээс (5MB) хэтэрсэн байна!`, false);
			}
		}
		form.setFieldsValue({
			[key]: value,
		});
	};

	const finalizeValues = async fieldValues => {
		const values = _.cloneDeep(fieldValues);
		for (const field of fields) {
			const { key } = field;
			if (key) {
				if (field.instance === 'image') {
					if (!values?.[key]?.[0].url) {
						values[key] = await fileUploader(values[key]) ?? undefined;
					} else {
						values[key] = values?.key?.[0]?.url;
					}
				}
				if (field.instance === 'file') {
					if (!values?.[key]?.[0].url) {
						values[key] = await fileUploader(values[key], true, 'file') ?? undefined;
					} else {
						values[key] = values?.key?.[0]?.url;
					}
				}
			}
		}
		return values;
	};

	return (
		<Spin spinning={loading}>
			<Form
				form={form}
				labelCol={{ span: 8 }}
				wrapperCol={{ span: 8 }}
				onFinish={handleSubmit}
				onValuesChange={handleFormValueChange}
				autoComplete="off"
				className="custom-form"
			>
				{getFields()}
				<Row type="flex" justify="center" align="middle">
					<Button style={{ marginRight: 3 }} onClick={onCancel} type="link">
						Буцах
					</Button>
					<Button htmlType="submit" style={{ marginLeft: 3 }} type="primary" loading={submitting}>
						Хадгалах
					</Button>
				</Row>
			</Form>
		</Spin>
	);
};

export default CustomForm;
